import React, { useMemo } from "react";

import isIntuit from "Intuit/isIntuit";
import { EmployeeCostsTable } from "QuotePage/Components/EmployeeCosts";
import { getEmployeeProfile, calculateNumEmployeeProfiles, calculateNumDependents } from "QuotePage/Selectors";
import { isRenewal, priceBreakdownURL } from "QuotePage/urls";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import BodyText from "Cobalt/BodyText";
import { EmptyState } from "Cobalt/EmptyState";
import { ReduxTriggerButton } from "Cobalt/Modal";
import { calculateSampleQuote } from "Common/EnterAges/Selectors";
import { Div } from "Common/HTML";
import { getPlanById } from "Common/PlanLoader/Selectors";

// Constants
const MAX_EMPLOYEE_PROFILES = 3;
const ANALYTICS_LOCATION = "expanding_plan_card";

const EmptyEmployeeCostsTable = () => (
  <Div>
    <BodyText pb={4} size="small" muted>
      * Initial quote for a 30 year old employee
    </BodyText>
    <EmptyState icon="user-plus" size="3x" mr={4}>
      <BodyText muted>Add employees and family to see exact rates</BodyText>
      <ReduxTriggerButton name="enterAgesModal">Add Employees</ReduxTriggerButton>
    </EmptyState>
  </Div>
);

const SeeCostsButton = ({ planId, children, onClick, ...props }) => {
  const plan = useSelector((state) => getPlanById(state, planId));
  const employeeProfile = useSelector(getEmployeeProfile);
  const numHiddenEmployees = calculateNumEmployeeProfiles(employeeProfile) - MAX_EMPLOYEE_PROFILES;
  /** # of employees not shown in the small costs table affects dynamic copy text */
  const linkText =
    isIntuit() || !isRenewal() || numHiddenEmployees <= 0
      ? "See Costs For All Employees"
      : `+${numHiddenEmployees} More Employee${numHiddenEmployees > 1 ? "s" : ""}`;

  const handleClick = (e) => {
    e.stopPropagation();
    const isSampleQuote = calculateSampleQuote(employeeProfile);
    AnalyticsTracking.trackEvent("employee_costs_viewed", {
      location: ANALYTICS_LOCATION,
      plan_id: plan.id,
      name: plan.raw_name,
      brand: plan.carrier,
      ages_entered: !isSampleQuote,
    });

    if (onClick) onClick(plan);
  };

  return (
    <Link {...props} to={priceBreakdownURL({ params: { planID: plan.id } })} onClick={handleClick}>
      {linkText}
    </Link>
  );
};

const EmployeeCostsSection = ({ planId, recommendationType, children, ...rest }) => {
  const plan = useSelector((state) => getPlanById(state, planId));

  // Logic for EmployeeCostsTable rows:
  // If only one employee
  // ...Show up to 1 spouse and 1 child, or 2 children if no spouse
  // ...If >3 linked to that employee policy, just show the combined price.
  // Else, show all employee costs
  const employeeProfile = useSelector(getEmployeeProfile);
  const numEmployeeProfiles = calculateNumEmployeeProfiles(employeeProfile);
  const numDependents = calculateNumDependents(employeeProfile);
  const costsTableRows = useMemo(() => {
    const showFamily = numEmployeeProfiles === 1;

    return employeeProfile.reduce((rows, employee, index) => {
      if (!employee.age) return rows; // early exit
      const employeeCost = plan?.employee_costs?.[index]?.self_cost || 0;

      // Add employee row
      rows.push({
        name: employee.name || `Employee ${index + 1}`,
        age: employee.age,
        cost: employeeCost,
        nested: false,
      });

      if (showFamily) {
        const showSpouse = showFamily && employeeProfile?.[0]?.spouse_age && numDependents < 3;
        const numChildren = employee.children?.length || 0;
        const showChildren = showFamily && numChildren && (showSpouse ? numChildren === 1 : numChildren === 2);

        if (showSpouse) {
          rows.push({
            name: "Spouse",
            age: employee.spouse_age,
            cost: plan.employee_costs?.[index]?.spouse_cost || 0,
            nested: true,
          });
        }

        if (showChildren) {
          employee.children.forEach((child, childIndex) => {
            rows.push({
              name: "Child",
              age: child,
              cost: plan.employee_costs?.[index]?.child_cost?.[childIndex] || 0,
              nested: true,
            });
          });
        }
      }

      return rows;
    }, []);
  }, [employeeProfile, plan]).slice(0, MAX_EMPLOYEE_PROFILES);

  return (
    <>
      <EmployeeCostsTable rows={costsTableRows} emptyComponent={<EmptyEmployeeCostsTable />} {...rest} />

      {costsTableRows.length > 0 && (
        <Div py={isIntuit() ? 3 : 4} px={isIntuit() ? 2 : 3}>
          <SeeCostsButton planId={planId} />
        </Div>
      )}
    </>
  );
};

export default EmployeeCostsSection;
